import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useCallback, useRef } from "react";

interface ChatUiPref {
  id: string;
  user_id: string;
  thread_id: string;
  meeting_card_minimized: boolean;
  meeting_last_seen_at: string | null;
  updated_at: string;
}

// Debounce delay for saving preferences (ms)
const SAVE_DEBOUNCE_MS = 1000;

// Debug logging (enable via localStorage.setItem('debugChatPrefs', 'true'))
const debugLog = (...args: unknown[]) => {
  if (typeof window !== "undefined" && localStorage.getItem("debugChatPrefs") === "true") {
    console.log("[useMeetingCardPrefs]", ...args);
  }
};

export function useMeetingCardPrefs(threadId: string | undefined) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Track last saved value to avoid redundant writes
  const lastSavedMinimized = useRef<boolean | null>(null);
  
  // Debounce timer ref
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: prefs, isLoading } = useQuery({
    queryKey: ["chat-ui-prefs", threadId, user?.id],
    queryFn: async () => {
      if (!threadId || !user?.id) return null;

      debugLog("Fetching prefs for thread:", threadId, "user:", user.id);

      const { data, error } = await supabase
        .from("chat_ui_prefs")
        .select("*")
        .eq("user_id", user.id)
        .eq("thread_id", threadId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching chat UI prefs:", error);
        return null;
      }

      // Update lastSaved ref with fetched value
      if (data) {
        lastSavedMinimized.current = data.meeting_card_minimized;
      }

      return data as ChatUiPref | null;
    },
    enabled: !!threadId && !!user?.id,
    staleTime: 30000, // 30 seconds
  });

  const toggleMinimizedMutation = useMutation({
    mutationFn: async (minimized: boolean) => {
      if (!threadId || !user?.id) {
        debugLog("Skipping save - missing thread or user", { threadId, userId: user?.id });
        throw new Error("Missing thread or user");
      }

      // Skip if value hasn't changed from last saved value
      if (lastSavedMinimized.current === minimized) {
        debugLog("Skipping save - value unchanged", { minimized, lastSaved: lastSavedMinimized.current });
        return null;
      }

      debugLog("Saving minimized state:", minimized, "for thread:", threadId);

      const { data, error } = await supabase
        .from("chat_ui_prefs")
        .upsert(
          {
            user_id: user.id,
            thread_id: threadId,
            meeting_card_minimized: minimized,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: "user_id,thread_id",
          }
        )
        .select()
        .single();

      if (error) {
        // Don't retry on auth errors
        if (error.message?.includes("No API key") || error.code === "401" || error.code === "403") {
          console.error("Auth error saving chat UI prefs - not retrying:", error);
          throw error;
        }
        throw error;
      }
      
      // Update last saved value on success
      lastSavedMinimized.current = minimized;
      debugLog("Successfully saved minimized state:", minimized);
      
      return data;
    },
    onMutate: async (minimized) => {
      // Optimistic update
      const queryKey = ["chat-ui-prefs", threadId, user?.id];
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<ChatUiPref | null>(queryKey);

      queryClient.setQueryData<ChatUiPref | null>(queryKey, (old) => {
        if (old) {
          return { ...old, meeting_card_minimized: minimized };
        }
        return {
          id: "temp",
          user_id: user?.id || "",
          thread_id: threadId || "",
          meeting_card_minimized: minimized,
          meeting_last_seen_at: null,
          updated_at: new Date().toISOString(),
        };
      });

      return { previous };
    },
    onError: (err, _, context) => {
      console.error("Error toggling meeting card minimized state:", err);
      if (context?.previous !== undefined) {
        queryClient.setQueryData(
          ["chat-ui-prefs", threadId, user?.id],
          context.previous
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["chat-ui-prefs", threadId, user?.id],
      });
    },
    // Don't retry on failure - prevents hammering the API
    retry: false,
  });

  const resetMinimizedMutation = useMutation({
    mutationFn: async () => {
      if (!threadId || !user?.id) {
        debugLog("Skipping reset - missing thread or user", { threadId, userId: user?.id });
        throw new Error("Missing thread or user");
      }

      // Skip if already expanded (not minimized)
      if (lastSavedMinimized.current === false) {
        debugLog("Skipping reset - already expanded");
        return null;
      }

      debugLog("Resetting minimized state for thread:", threadId);

      const { error } = await supabase
        .from("chat_ui_prefs")
        .upsert(
          {
            user_id: user.id,
            thread_id: threadId,
            meeting_card_minimized: false,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: "user_id,thread_id",
          }
        );

      if (error) {
        // Don't retry on auth errors
        if (error.message?.includes("No API key") || error.code === "401" || error.code === "403") {
          console.error("Auth error resetting chat UI prefs - not retrying:", error);
          throw error;
        }
        throw error;
      }
      
      // Update last saved value on success
      lastSavedMinimized.current = false;
      debugLog("Successfully reset minimized state");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chat-ui-prefs", threadId, user?.id],
      });
    },
    // Don't retry on failure
    retry: false,
  });

  // Debounced toggle function - memoized to prevent re-renders from triggering effects
  const toggleMinimized = useCallback((minimized: boolean) => {
    // Clear any pending debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // Skip if value hasn't changed
    if (lastSavedMinimized.current === minimized) {
      debugLog("Toggle skipped - no change", { minimized, lastSaved: lastSavedMinimized.current });
      return;
    }
    
    // Debounce the actual mutation
    debounceTimer.current = setTimeout(() => {
      toggleMinimizedMutation.mutate(minimized);
    }, SAVE_DEBOUNCE_MS);
  }, [toggleMinimizedMutation]);

  // Memoized reset function - prevents useEffect dependency loops
  const resetMinimized = useCallback(() => {
    // Skip if already expanded
    if (lastSavedMinimized.current === false) {
      debugLog("Reset skipped - already expanded");
      return;
    }
    
    // Clear any pending debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // Debounce the reset
    debounceTimer.current = setTimeout(() => {
      resetMinimizedMutation.mutate();
    }, SAVE_DEBOUNCE_MS);
  }, [resetMinimizedMutation]);

  return {
    isMinimized: prefs?.meeting_card_minimized ?? false,
    isLoading,
    toggleMinimized,
    resetMinimized,
    isToggling: toggleMinimizedMutation.isPending,
  };
}
